import { Box3, Group, Mesh, type Object3D, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { disposeObject3DResources as disposeMeshes } from "../models/shared-model-resources";

export enum RackPostDirection {
  Front = "front",
  Back = "back"
}

type RackPostPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type RackPostOptions = {
  angleIronHeightCellCount?: number;
  angleIronYPositions?: number[];
  direction?: RackPostDirection;
  height?: number;
  position?: RackPostPosition;
};

const angleIronOffset = { x: 0.034, z: 0.0225 };
const frontModelRotationY = -Math.PI / 2;

type RackPostAngleIronPair = {
  height: number;
  left: Group;
  right: Group;
};

export class RackPost extends Group {
  private angleIronHeightCellCount?: number;
  private angleIronYPositions: number[];
  private angleIronPairs: RackPostAngleIronPair[] = [];
  private direction: RackPostDirection;
  private height: number;
  private postBaseY = 0;
  private postBodyHeight = 0;
  private postContent?: Object3D;
  private postOriginalBodyHeight = 0;

  constructor({
    angleIronHeightCellCount,
    angleIronYPositions = [],
    direction = RackPostDirection.Front,
    height = 1,
    position = {}
  }: RackPostOptions = {}) {
    super();
    this.angleIronHeightCellCount = angleIronHeightCellCount;
    this.angleIronYPositions = angleIronYPositions;
    this.direction = direction;
    this.height = height;
    this.name = `rack-post-${direction}-${height}`;
    this.setRackPosition(position);
  }

  static async create(options: RackPostOptions = {}, loader: ModelAssetLoader = modelAssetLibrary) {
    const post = new RackPost(options);
    await post.load(loader);
    return post;
  }

  getBodyWidth() {
    if (!this.postContent) {
      return 0;
    }

    this.postContent.updateWorldMatrix(true, true);
    return new Box3().setFromObject(this.postContent).getSize(new Vector3()).x;
  }

  getBodyDepth() {
    if (!this.postContent) {
      return 0;
    }

    this.postContent.updateWorldMatrix(true, true);
    return new Box3().setFromObject(this.postContent).getSize(new Vector3()).z;
  }

  getPostBaseY() {
    return this.postBaseY;
  }

  getPostBodyHeight() {
    return this.postBodyHeight;
  }

  setAngleIronHeightCellCount(
    heightCellCount: number,
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    this.angleIronHeightCellCount = heightCellCount;
    return this.reloadAngleIrons(loader);
  }

  setAngleIronYPositions(
    angleIronYPositions: number[],
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    this.angleIronHeightCellCount = undefined;
    this.angleIronYPositions = angleIronYPositions;
    return this.reloadAngleIrons(loader);
  }

  setDirection(direction: RackPostDirection) {
    this.direction = direction;
    this.name = `rack-post-${direction}-${this.height}`;
    this.applyDirection();
  }

  setHeight(height: number) {
    this.height = height;
    this.name = `rack-post-${this.direction}-${height}`;

    if (this.postContent) {
      this.postContent.scale.y = height;
      this.postBodyHeight = this.postOriginalBodyHeight * height;
      this.updateAngleIronYPositions();
    }
  }

  setRackPosition(position: RackPostPosition) {
    this.position.set(
      position.x ?? this.position.x,
      position.y ?? this.position.y,
      position.z ?? this.position.z
    );
  }

  private async load(loader: ModelAssetLoader) {
    const [rawPostContent, rawBasePlateContent] = await Promise.all([
      loader.load(ModelAsset.RackPost),
      loader.load(ModelAsset.BasePlate)
    ]);

    applyFrontModelRotation(rawPostContent);
    applyFrontModelRotation(rawBasePlateContent);
    enableShadows(rawPostContent);
    enableShadows(rawBasePlateContent);

    const postContent = createBottomOriginGroup(rawPostContent);
    const basePlateContent = createBottomOriginGroup(rawBasePlateContent);

    this.postContent = postContent.object;
    this.postBaseY = basePlateContent.height;
    this.postOriginalBodyHeight = postContent.height;
    this.postContent.position.y = this.postBaseY;
    this.applyDirection();

    this.add(basePlateContent.object, this.postContent);
    this.setHeight(this.height);
    await this.reloadAngleIrons(loader);
  }

  private applyDirection() {
    this.rotation.y = getDirectionRotationY(this.direction);
  }

  private async reloadAngleIrons(loader: ModelAssetLoader) {
    this.angleIronPairs.forEach((pair) => {
      this.remove(pair.right, pair.left);
      disposeMeshes(pair.right);
      disposeMeshes(pair.left);
    });
    this.angleIronPairs = await Promise.all(
      Array.from({ length: this.getAngleIronCount() }, (_, index) =>
        loadAngleIronPair(loader, index)
      )
    );
    const angleIrons = this.angleIronPairs.flatMap((pair) => [pair.right, pair.left]);
    if (angleIrons.length > 0) {
      this.add(...angleIrons);
    }
    this.updateAngleIronYPositions();
  }

  private updateAngleIronYPositions() {
    this.getAngleIronYPositions().forEach((targetY, index) => {
      const pair = this.angleIronPairs[index];

      if (!pair) {
        return;
      }

      const y = this.angleIronHeightCellCount === undefined ? targetY : targetY - pair.height / 2;

      pair.right.position.set(angleIronOffset.x, y, angleIronOffset.z);
      pair.left.position.set(-angleIronOffset.x, y, angleIronOffset.z);
    });
  }

  private getAngleIronCount() {
    if (this.angleIronHeightCellCount === undefined) {
      return this.angleIronYPositions.length;
    }

    return Math.max(this.angleIronHeightCellCount - 1, 0);
  }

  private getAngleIronYPositions() {
    if (this.angleIronHeightCellCount === undefined) {
      return this.angleIronYPositions;
    }

    const cellHeight = this.postBodyHeight / this.angleIronHeightCellCount;

    return Array.from(
      { length: this.angleIronHeightCellCount - 1 },
      (_, index) => this.postBaseY + cellHeight * (index + 1)
    );
  }
}

async function loadAngleIronPair(loader: ModelAssetLoader, index: number) {
  const [rightAngleIron, leftAngleIron] = await Promise.all([
    loadAngleIron(loader, `right-angle-iron-${index}`),
    loadAngleIron(loader, `left-angle-iron-${index}`)
  ]);

  leftAngleIron.scale.x = -1;

  return {
    height: getObjectHeight(rightAngleIron),
    left: leftAngleIron,
    right: rightAngleIron
  };
}

async function loadAngleIron(loader: ModelAssetLoader, name: string) {
  const content = await loader.load(ModelAsset.AngleIron);

  applyFrontModelRotation(content);
  enableShadows(content);
  const angleIron = createBottomOriginGroup(content).object;
  angleIron.name = name;

  return angleIron;
}

function applyFrontModelRotation(object: Object3D) {
  object.rotation.y = frontModelRotationY;
}

function getDirectionRotationY(direction: RackPostDirection) {
  if (direction === RackPostDirection.Back) {
    return Math.PI;
  }

  return 0;
}

function enableShadows(object: Object3D) {
  object.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

function createBottomOriginGroup(content: Object3D) {
  content.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(content);
  const center = bounds.getCenter(new Vector3());
  const group = new Group();

  content.position.x -= center.x;
  content.position.y -= bounds.min.y;
  content.position.z -= center.z;
  group.add(content);
  group.updateWorldMatrix(true, true);

  return {
    height: getObjectHeight(group),
    object: group
  };
}

function getObjectHeight(object: Object3D) {
  object.updateMatrixWorld(true);
  return new Box3().setFromObject(object).getSize(new Vector3()).y;
}
